import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    Switch,
    Tabs,
    Tab,
    Card,
    CardBody,
    Chip,
    Alert,
    Avatar,
    addToast,
    Listbox,
    ListboxItem,
    ScrollShadow,
    Autocomplete,
    AutocompleteItem
} from "@heroui/react";
import {
    Bell,
    BellOff,
    Send,
    Trash2,
    Plus,
    Tv,
    User,
    ExternalLink,
    RefreshCw,
    Copy,
    Pencil,
    HelpCircle
} from "lucide-react";
import MixToolTip from "./MixToolTip";
import axios from "axios";
import Markdown from "react-markdown";
import DOC from "../assets/webpush.md?raw";
import { CheckIcon } from "../pages/ChatPage";

// 硬编码 VAPID 公钥
const PUBLIC_KEY = "BBN4zAUvf2kLsKA5lyWqnRO_AFayqrBc30AnL2VO9dXaRk7TkRMkGBo5cxnFlVGKzor332tTrtMg80IbPRg5OE0";
const STORAGE_PROFILE_KEY = "bili_push_profile_uuid";
const SW_URL = '/sw.js?v=1';

// 联想搜索与样式辅助函数（与 IndexPage 逻辑一致）
const AVATAR_API = (typeof window !== "undefined" && window.AVATAR_API) || 'https://workers.vrp.moe/bilibili/avatar/';

function isNumber(str) {
    return !isNaN(Number(str)) && String(str).trim() !== "";
}

const getGuardIcon = (level) => {
    if (typeof window !== "undefined" && window.getGuardIcon) {
        return window.getGuardIcon(level);
    }
    const array = ["", "https://i1.hdslb.com/bfs/static/blive/blfe-live-room/static/img/logo-1.b718085..png", "https://i1.hdslb.com/bfs/static/blive/blfe-live-room/static/img/logo-2.d43d078..png", "https://i1.hdslb.com/bfs/static/blive/blfe-live-room/static/img/logo-3.6d2f428..png"];
    return array[level] || "";
};

const getColor = (level) => {
    if (typeof window !== "undefined" && window.getColor) {
        return window.getColor(level);
    }
    if (level <= 10) return "#727BB5";
    if (level <= 20) return "#CF86B2";
    if (level <= 30) return "#5EC0F7";
    if (level <= 40) return "#6992FF";
    if (level <= 50) return "#AA78F1";
    return "#ED5674";
};

// Web Push 标准 Base64 URL 转 Uint8Array
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

// 获取或初始化本地前端生成的 UUID
function getOrInitProfileUUID() {
    let uuid = localStorage.getItem(STORAGE_PROFILE_KEY);
    if (!uuid) {
        uuid = typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : 'profile_' + Math.random().toString(36).slice(2) + Date.now();
        localStorage.setItem(STORAGE_PROFILE_KEY, uuid);
    }
    return uuid;
}

// 检测是否为 iOS / iPadOS 设备
function checkIsIOS() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function PushModal({ open, setOpen }) {
    // 基础状态
    const [isSupported, setIsSupported] = useState(true);
    const [permission, setPermission] = useState('default');
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [profileUUID, setProfileUUID] = useState(getOrInitProfileUUID);
    const [editUUID, setEditUUID] = useState('');
    const [isEditingUUID, setIsEditingUUID] = useState(false);
    const [deviceCount, setDeviceCount] = useState(0);

    // 交互状态
    const [loading, setLoading] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("livers");

    // 配置数据：主播列表 & 用户列表
    const [livers, setLivers] = useState([]);
    const [users, setUsers] = useState([]);
    const [userInfoMap, setUserInfoMap] = useState({});

    // 批量加载用户信息（头像与用户名，带后端 24 小时内存缓存）
    const fetchUsersInfo = useCallback(async (uids) => {
        const validUIDs = (uids || []).map(u => String(u).trim()).filter(Boolean);
        if (validUIDs.length === 0) return;
        try {
            const res = await axios.post('/api/push_srv/users_info', { uids: validUIDs });
            if (res.data?.code === 200 && res.data.data) {
                setUserInfoMap(prev => ({ ...prev, ...res.data.data }));
            }
        } catch (e) {
            console.error("Fetch users info error:", e);
        }
    }, []);

    // 新增输入框与自动补全联想状态
    const [newLiverInput, setNewLiverInput] = useState('');
    const [newUserInput, setNewUserInput] = useState('');
    const [liverSuggestions, setLiverSuggestions] = useState([]);
    const [userSuggestions, setUserSuggestions] = useState([]);
    const [liverLoading, setLiverLoading] = useState(false);
    const [userLoading, setUserLoading] = useState(false);

    // 1. 检查浏览器环境与推送订阅状态
    const checkStatus = useCallback(async () => {
        if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
            setIsSupported(false);
            return;
        }
        setIsSupported(true);
        setPermission(Notification.permission);

        try {
            const reg = await navigator.serviceWorker.getRegistration() || (await navigator.serviceWorker.getRegistrations?.())?.[0];
            if (reg) {
                const sub = await reg.pushManager.getSubscription();
                setIsSubscribed(!!sub);
            } else {
                setIsSubscribed(false);
            }
        } catch (e) {
            console.error("Check push status error:", e);
            setIsSubscribed(false);
        }
    }, []);

    // 2. 从后端加载当前 Profile 的配置内容
    const loadProfileContent = useCallback(async (uuid) => {
        if (!uuid) return;
        try {
            const res = await axios.get(`/api/push_srv/profile?uuid=${encodeURIComponent(uuid)}`);
            if (res.data && res.data.content) {
                try {
                    const parsed = JSON.parse(res.data.content);
                    const lList = Array.isArray(parsed.livers) ? parsed.livers : [];
                    const uList = Array.isArray(parsed.users) ? parsed.users : [];
                    setLivers(lList);
                    setUsers(uList);

                    // 批量加载用户和主播的头像与用户名
                    const allUIDs = [...lList.map(l => l.uid), ...uList.map(u => u.uid)];
                    fetchUsersInfo(allUIDs);
                } catch {
                    setLivers([]);
                    setUsers([]);
                }
            } else {
                setLivers([]);
                setUsers([]);
            }

            // 获取绑定设备数
            const devRes = await axios.get(`/api/push_srv/devices?profile=${encodeURIComponent(uuid)}`);
            if (devRes.data && Array.isArray(devRes.data.devices)) {
                setDeviceCount(devRes.data.devices.length);
            }
        } catch (e) {
            // 若未找到 profile 则保持空
            if (e.response?.status !== 404) {
                console.error("Load profile content error:", e);
            }
        }
    }, [fetchUsersInfo]);

    // 3. 将修改后的配置保存到后端
    const syncProfileToBackend = useCallback(async (newLi, newUs, targetUUID = profileUUID) => {
        try {
            const contentObj = {
                livers: newLi,
                users: newUs
            };
            await axios.post('/api/push_srv/profile', {
                uuid: targetUUID,
                content: JSON.stringify(contentObj)
            });
        } catch (e) {
            console.error("Failed to sync profile:", e);
            addToast({
                title: "同步失败",
                description: e.response?.data?.error || e.message,
                color: "danger"
            });
        }
    }, [profileUUID]);

    // 弹窗打开时加载状态和配置
    useEffect(() => {
        if (open) {
            const currentUUID = getOrInitProfileUUID();
            setProfileUUID(currentUUID);
            checkStatus();
            loadProfileContent(currentUUID);
        }
    }, [open, checkStatus, loadProfileContent]);

    // 4. 开启 / 关闭推送订阅切换
    const handleToggleSubscription = async () => {
        if (!isSupported) {
            const isIOS = checkIsIOS();
            addToast({
                title: isIOS ? "请将网页添加到主屏幕" : "环境不支持",
                description: isIOS
                    ? "iOS 设备请通过 Safari【分享】➔【添加到主屏幕】后在桌面打开即可开启推送"
                    : "当前浏览器环境不支持 Web Push",
                color: isIOS ? "warning" : "danger"
            });
            return;
        }

        setLoading(true);
        try {
            if (isSubscribed) {
                // 取消订阅
                const reg = await navigator.serviceWorker.ready;
                const sub = await reg.pushManager.getSubscription();
                if (sub) {
                    await sub.unsubscribe();
                    await axios.post('/api/push_srv/unsubscribe', { endpoint: sub.endpoint });
                }
                setIsSubscribed(false);
                addToast({
                    title: "已关闭推送",
                    description: "本设备将不再接收浏览器通知",
                    color: "warning"
                });
                checkStatus();
            } else {
                // 请求通知权限
                const perm = await Notification.requestPermission();
                setPermission(perm);
                if (perm !== 'granted') {
                    addToast({
                        title: "权限被拒绝",
                        description: "请在浏览器设置中开启此站点的通知权限",
                        color: "danger"
                    });
                    setLoading(false);
                    return;
                }

                // 注册 Service Worker
                await navigator.serviceWorker.register(SW_URL);
                const reg = await navigator.serviceWorker.ready;

                // 订阅 Web Push（使用硬编码公钥）
                const sub = await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(PUBLIC_KEY)
                });

                const subJson = sub.toJSON();

                // 上报订阅至后端，绑定当前前端 Profile UUID
                await axios.post('/api/push_srv/subscribe', {
                    profile: profileUUID,
                    endpoint: subJson.endpoint,
                    keys: subJson.keys
                });

                setIsSubscribed(true);
                addToast({
                    title: "推送已开启",
                    description: "本设备已成功注册 Web Push 接收服务",
                    color: "success"
                });
                checkStatus();
                await loadProfileContent(profileUUID);
            }
        } catch (e) {
            console.error("Toggle subscription error:", e);
            addToast({
                title: "操作失败",
                description: e.response?.data?.error || e.message,
                color: "danger"
            });
        } finally {
            setLoading(false);
        }
    };

    // 5. 发送测试通知
    const handleSendTest = async () => {
        if (!isSubscribed) {
            addToast({
                title: "未开启推送",
                description: "请先开启推送开关后再发送测试通知",
                color: "warning"
            });
            return;
        }

        setTestLoading(true);
        try {
            const res = await axios.post('/api/push_srv/test', {
                profile: profileUUID,
                title: "测试通知",
                body: "这是一条测试推送，说明Web Push链路配置完全正常"
            });

            if (res.data?.code === 200) {
                addToast({
                    title: "测试通知已发出",
                    description: "若未弹出，请检查设备是否处于免打扰模式或未挂载代理",
                    color: "success"
                });
            } else {
                addToast({
                    title: "测试发送提示",
                    description: res.data?.error || "发送完成",
                    color: "warning"
                });
            }
        } catch (e) {
            addToast({
                title: "发送失败",
                description: e.response?.data?.error || e.message,
                color: "danger"
            });
        } finally {
            setTestLoading(false);
        }
    };

    // 6. 切换/同步其他设备的 Profile UUID
    const handleApplyUUID = async () => {
        const trimmed = editUUID.trim();
        if (!trimmed) {
            addToast({ title: "请输入有效的 UUID", color: "warning" });
            return;
        }
        if (trimmed === profileUUID) {
            setIsEditingUUID(false);
            return;
        }

        setLoading(true);
        try {
            // 保存至本地存储
            localStorage.setItem(STORAGE_PROFILE_KEY, trimmed);
            setProfileUUID(trimmed);

            // 如果当前设备处于已订阅状态，向后端重新绑定当前 endpoint 到新 UUID
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                const subJson = sub.toJSON();
                await axios.post('/api/push_srv/subscribe', {
                    profile: trimmed,
                    endpoint: subJson.endpoint,
                    keys: subJson.keys
                });
            }

            // 加载新 UUID 对应的配置
            await loadProfileContent(trimmed);

            setIsEditingUUID(false);
            addToast({
                title: "UUID 切换成功",
                description: "已同步该 UUID 下的关注列表与推送设置",
                color: "success"
            });
        } catch (e) {
            addToast({
                title: "切换失败",
                description: e.response?.data?.error || e.message,
                color: "danger"
            });
        } finally {
            setLoading(false);
        }
    };

    // 搜索主播联想（逻辑与 IndexPage 一致，支持精确 UID 与名称搜索）
    useEffect(() => {
        const text = newLiverInput.trim();
        if (!text) {
            setLiverSuggestions([]);
            return;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setLiverLoading(true);
            try {
                const cleanText = text.replace(/^UID:\s*/i, "");
                if (isNumber(cleanText)) {
                    // 先查本地精确 UID
                    const searchRes = await axios.get(`/api/search?api=1&type=uid&key=${cleanText}`);
                    if (!cancelled && searchRes.data?.data?.length) {
                        const item = searchRes.data.data[0];
                        setLiverSuggestions([{
                            Type: "Liver",
                            UName: item.UName,
                            Fans: item.ExtraInt,
                            UID: item.UID,
                        }]);
                        return;
                    }
                    // 兜底查 users_info 获取头像与真实名称
                    const infoRes = await axios.post('/api/push_srv/users_info', { uids: [cleanText] });
                    if (!cancelled && infoRes.data?.data?.[cleanText]) {
                        const info = infoRes.data.data[cleanText];
                        setLiverSuggestions([{
                            Type: "Liver",
                            UName: info.name,
                            UID: cleanText,
                            face: info.face
                        }]);
                    } else if (!cancelled) {
                        setLiverSuggestions([]);
                    }
                } else {
                    // 主播名模糊搜索
                    const res = await axios.get(`/api/search?api=1&type=name&key=${encodeURIComponent(text)}`);
                    if (!cancelled) {
                        const list = (res.data?.data ?? []).map(item => ({
                            ...item,
                            Type: "Liver",
                            Fans: item.ExtraInt,
                        }));
                        setLiverSuggestions(list);
                    }
                }
            } catch (err) {
                console.error("Liver search error:", err);
                if (!cancelled) setLiverSuggestions([]);
            } finally {
                if (!cancelled) setLiverLoading(false);
            }
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [newLiverInput]);

    // 搜索用户联想（逻辑与 IndexPage 一致，支持精确 UID 与观众名称搜索）
    useEffect(() => {
        const text = newUserInput.trim();
        if (!text) {
            setUserSuggestions([]);
            return;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setUserLoading(true);
            try {
                const cleanText = text.replace(/^UID:\s*/i, "");
                if (isNumber(cleanText)) {
                    const infoRes = await axios.post('/api/push_srv/users_info', { uids: [cleanText] });
                    if (!cancelled && infoRes.data?.data?.[cleanText]) {
                        const info = infoRes.data.data[cleanText];
                        setUserSuggestions([{
                            Type: "Watcher",
                            UName: info.name,
                            UID: cleanText,
                            face: info.face
                        }]);
                    } else if (!cancelled) {
                        setUserSuggestions([]);
                    }
                } else {
                    // 用户名模糊搜索
                    const res = await axios.get(`/api/search?api=1&type=watcher-name&key=${encodeURIComponent(text)}`);
                    if (!cancelled) {
                        const list = (res.data?.data ?? []).map(item => ({
                            ...item,
                            MedalType: item.Type,
                            Type: "Watcher",
                        }));
                        setUserSuggestions(list);
                    }
                }
            } catch (err) {
                console.error("Watcher search error:", err);
                if (!cancelled) setUserSuggestions([]);
            } finally {
                if (!cancelled) setUserLoading(false);
            }
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [newUserInput]);

    // 主播列表操作
    const handleAddLiver = useCallback((selectedItem = null) => {
        let uid = '';
        let info = null;
        if (selectedItem && selectedItem.UID) {
            uid = String(selectedItem.UID).trim();
            info = selectedItem;
        } else {
            const raw = newLiverInput.trim().replace(/^UID:\s*/i, "");
            if (isNumber(raw)) {
                uid = raw;
            } else if (liverSuggestions.length > 0) {
                uid = String(liverSuggestions[0].UID).trim();
                info = liverSuggestions[0];
            }
        }
        if (!uid) {
            if (newLiverInput.trim()) {
                addToast({ title: "请输入有效的 UID 或从下拉建议中选择", color: "warning" });
            }
            return;
        }

        setLivers(prev => {
            if (prev.some(item => String(item.uid) === uid)) {
                addToast({ title: "已在关注列表中", color: "warning" });
                return prev;
            }
            const updated = [...prev, { uid: uid, live: true, dynamic: true }];
            syncProfileToBackend(updated, users);
            addToast({ title: "添加关注主播成功", color: "success" });
            return updated;
        });

        setNewLiverInput('');
        setLiverSuggestions([]);
        if (info && info.UName) {
            setUserInfoMap(prev => ({
                ...prev,
                [uid]: {
                    name: info.UName,
                    face: info.face || `${AVATAR_API}${uid}`
                }
            }));
        }
        fetchUsersInfo([uid]);
    }, [newLiverInput, liverSuggestions, users, syncProfileToBackend, fetchUsersInfo]);

    const handleToggleLiver = (idx, field, val) => {
        const updated = [...livers];
        updated[idx] = { ...updated[idx], [field]: val };
        setLivers(updated);
        syncProfileToBackend(updated, users);
    };

    const handleRemoveLiver = (idx) => {
        const updated = livers.filter((_, i) => i !== idx);
        setLivers(updated);
        syncProfileToBackend(updated, users);
    };

    // 用户列表操作
    const handleAddUser = useCallback((selectedItem = null) => {
        let uid = '';
        let info = null;
        if (selectedItem && selectedItem.UID) {
            uid = String(selectedItem.UID).trim();
            info = selectedItem;
        } else {
            const raw = newUserInput.trim().replace(/^UID:\s*/i, "");
            if (isNumber(raw)) {
                uid = raw;
            } else if (userSuggestions.length > 0) {
                uid = String(userSuggestions[0].UID).trim();
                info = userSuggestions[0];
            }
        }
        if (!uid) {
            if (newUserInput.trim()) {
                addToast({ title: "请输入有效的 UID 或从下拉建议中选择", color: "warning" });
            }
            return;
        }

        setUsers(prev => {
            if (prev.some(item => String(item.uid) === uid)) {
                addToast({ title: "已在关注列表中", color: "warning" });
                return prev;
            }
            const updated = [...users, { uid: uid, danmaku: true, enter: true }];
            syncProfileToBackend(livers, updated);
            addToast({ title: "添加关注用户成功", color: "success" });
            return updated;
        });

        setNewUserInput('');
        setUserSuggestions([]);
        if (info && info.UName) {
            setUserInfoMap(prev => ({
                ...prev,
                [uid]: {
                    name: info.UName,
                    face: info.face || `${AVATAR_API}${uid}`
                }
            }));
        }
        fetchUsersInfo([uid]);
    }, [newUserInput, userSuggestions, livers, syncProfileToBackend, fetchUsersInfo]);

    const handleToggleUser = (idx, field, val) => {
        const updated = [...users];
        updated[idx] = { ...updated[idx], [field]: val };
        setUsers(updated);
        syncProfileToBackend(livers, updated);
    };

    const handleRemoveUser = (idx) => {
        const updated = users.filter((_, i) => i !== idx);
        setUsers(updated);
        syncProfileToBackend(livers, updated);
    };

    return (
        <Modal
            isOpen={open}
            onOpenChange={(val) => setOpen(val)}
            size="2xl"
            scrollBehavior={'inside'}

        >
            <ModalContent>
                {(onClose) => (
                    <>
                        <ModalHeader className="flex items-center justify-between gap-3 border-b border-default-100 pb-3 pr-8">
                            <div className="flex items-center gap-2">
                                <span>消息推送</span>
                                <MixToolTip
                                    content={
                                        <div className="max-w-xs sm:max-w-sm p-1.5 ">
                                            <Markdown
                                                components={{
                                                    h3: ({ node, ...props }) => (
                                                        <div className=" text-foreground text-[16px] mt-2.5 " {...props} />
                                                    ),
                                                    ul: ({ node, ...props }) => (
                                                        <ul className="list-disc pl-4 space-y-1 text-default-600 my-1 " {...props} />
                                                    ),
                                                }}
                                            >
                                                {DOC}
                                            </Markdown>
                                        </div>
                                    }
                                >
                                    <span className="inline-flex items-center justify-center cursor-pointer text-default-400 hover:text-default-600 transition-colors p-0.5 rounded-full hover:bg-default-100">
                                        <HelpCircle className="w-4 h-4" />
                                    </span>
                                </MixToolTip>
                            </div>
                            <div className="flex items-center gap-2">
                                {isSubscribed && (
                                    <Button
                                        size="sm"
                                        variant="light"
                                        color="secondary"
                                        className="h-7 text-xs px-2"
                                        onPress={handleSendTest}
                                        isLoading={testLoading}
                                        startContent={<Send size={12} />}
                                    >
                                        测试
                                    </Button>
                                )}
                                <Switch
                                    size="sm"
                                    isSelected={isSubscribed}
                                    isDisabled={!isSupported || permission === 'denied' || loading}
                                    onValueChange={handleToggleSubscription}
                                    color="primary"
                                    aria-label="消息推送开关"
                                />
                            </div>
                        </ModalHeader>

                        <ModalBody className="gap-4 py-4">
                            {/* 异常环境提示 */}
                            {!isSupported && (
                                checkIsIOS() ? (
                                    <Alert
                                        color="warning"
                                        title="请将网页添加到主屏幕"
                                        description="iOS 系统需将本站添加到主屏幕后才能开启推送：请点击 Safari 底部【分享】图标 ➔ 选择【添加到主屏幕】，随后在主屏幕打开即可开启通知。"
                                    />
                                ) : (
                                    <Alert
                                        color="danger"
                                        title="浏览器不支持 Web Push"
                                        description="当前环境（如微信内置或部分极简浏览器）不支持标准 PushManager 服务。"
                                    />
                                )
                            )}
                            {isSupported && permission === "denied" && (
                                <Alert
                                    color="warning"
                                    title="通知权限已被禁止"
                                    description="浏览器已阻止向此页面发送通知。请点击地址栏左侧锁头图标，将【通知】权限设为【允许】后刷新页面。"
                                />
                            )}

                            {/* 设备推送状态与同步卡片（暂时注释） */}
                            {/*
                            <Card className="bg-default-50/60 dark:bg-default-100/20 border border-default-200">
                                <CardBody className="gap-3.5 p-3.5">
                                    <div className="flex items-center justify-between">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-sm font-semibold text-default-800">
                                                本设备通知开关
                                            </span>
                                        </div>
                                        <Switch
                                            isSelected={isSubscribed}
                                            isDisabled={!isSupported || permission === 'denied' || loading}
                                            onValueChange={handleToggleSubscription}
                                            color="primary"
                                            size="md"
                                        />
                                    </div>

                                    <div className="flex flex-col gap-2 pt-2 border-t border-default-200/60">
                                        <div className="flex flex-col gap-1.5 bg-default-100/60 dark:bg-default-100/40 p-2.5 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <span className="text-[11px] ">
                                                    同步码
                                                </span>
                                                <Button
                                                    isIconOnly
                                                    size="sm"
                                                    variant="light"
                                                    className="h-6 w-6 min-w-6 text-default-400 hover:text-default-700"
                                                    onPress={() => {
                                                        navigator.clipboard.writeText(profileUUID);
                                                        addToast({
                                                            title: "已复制同步码",
                                                            description: profileUUID,
                                                            color: "success",
                                                            timeout: 2000
                                                        });
                                                    }}
                                                    title="复制同步码"
                                                >
                                                    <Copy size={13} />
                                                </Button>
                                            </div>

                                            {!isEditingUUID ? (
                                                <div
                                                    onClick={() => {
                                                        setEditUUID(profileUUID);
                                                        setIsEditingUUID(true);
                                                    }}
                                                    className="flex items-center justify-between group cursor-pointer py-1 px-2 rounded-md hover:bg-default-200/50 transition-colors border border-transparent hover:border-default-300/60"
                                                    title="点击修改同步码"
                                                >
                                                    <span className="text-xs font-mono text-default-700 font-semibold truncate select-all">
                                                        {profileUUID}
                                                    </span>
                                                    <div className="flex items-center gap-1 text-[11px] text-default-400 group-hover:text-primary transition-colors shrink-0 ml-2">
                                                        <Pencil size={12} />
                                                        <span>点击修改</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 pt-0.5">
                                                    <Input
                                                        size="sm"
                                                        value={editUUID}
                                                        onValueChange={setEditUUID}
                                                        placeholder="输入或粘贴目标 Profile UUID..."
                                                        className="font-mono text-xs flex-1"
                                                        autoFocus
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleApplyUUID();
                                                            if (e.key === 'Escape') setIsEditingUUID(false);
                                                        }}
                                                    />
                                                    <Button
                                                        size="sm"
                                                        color="primary"
                                                        onPress={handleApplyUUID}
                                                        isLoading={loading}
                                                    >
                                                        保存
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="light"
                                                        onPress={() => setIsEditingUUID(false)}
                                                    >
                                                        取消
                                                    </Button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between text-xs text-default-400 px-1 pt-1">
                                            <span>
                                                已绑定设备数：<strong className="text-default-700">{deviceCount}</strong> 台
                                            </span>
                                            <Button
                                                size="sm"
                                                variant="light"
                                                color="secondary"
                                                className="h-7 text-xs px-2.5"
                                                onPress={handleSendTest}
                                                isLoading={testLoading}
                                                isDisabled={!isSubscribed}
                                                startContent={<Send size={12} />}
                                            >
                                                发送测试通知
                                            </Button>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                            */}

                            {/* 关注内容管理 Tabs */}
                            <Tabs
                                selectedKey={activeTab}
                                onSelectionChange={setActiveTab}
                                fullWidth
                                color="primary"
                                className="mt-1"
                            >
                                {/* 主播关注列表 */}
                                <Tab
                                    key="livers"
                                    title={
                                        <div className="flex items-center gap-2">
                                            <Tv size={15} />
                                            <span>关注主播 ({livers.length})</span>
                                        </div>
                                    }
                                >
                                    <div className="flex flex-col gap-3 pt-2">
                                        {/* 快速添加栏 */}
                                        <div className="flex items-center gap-2">
                                            <Autocomplete
                                                aria-label="输入主播名称或 UID"
                                                placeholder="输入主播名称或 UID..."
                                                className="flex-1"
                                                size="sm"
                                                isLoading={liverLoading}
                                                inputValue={newLiverInput}
                                                onInputChange={(val) => setNewLiverInput(val)}
                                                onSelectionChange={(key) => {
                                                    if (key) {
                                                        const item = liverSuggestions.find(s => String(s.UID) === String(key));
                                                        handleAddLiver(item || { UID: key });
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleAddLiver();
                                                    }
                                                }}
                                                allowsCustomValue={true}
                                                isVirtualized={false}
                                                popoverProps={{
                                                    className: "z-[60]"
                                                }}
                                            >
                                                {liverSuggestions.map((e) => (
                                                    <AutocompleteItem
                                                        key={String(e.UID)}
                                                        textValue={`${e.UName} UID:${e.UID}`}
                                                    >
                                                        <div className="flex flex-row items-center">
                                                            <div className="flex flex-col">
                                                                <Avatar
                                                                    src={e.face || `${AVATAR_API}${e.UID}`}
                                                                    className="mr-2"
                                                                />
                                                            </div>
                                                            <div>
                                                                <p className="font-bold">{e.UName}</p>
                                                                <p className="text-small text-default-400">
                                                                    {e.Fans ? `${Number(e.Fans).toLocaleString()} Fans` : `UID: ${e.UID}`}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </AutocompleteItem>
                                                ))}
                                            </Autocomplete>
                                            <Button
                                                size="sm"
                                                color="primary"
                                                onPress={() => handleAddLiver()}
                                                startContent={<Plus size={15} />}
                                                className="shrink-0 h-8"
                                            >
                                                添加
                                            </Button>
                                        </div>

                                        {/* 列表渲染 */}
                                        <ScrollShadow className="w-full max-h-64 border border-default-200/80 dark:border-default-100/30 rounded-xl p-1 bg-default-50/50 dark:bg-default-100/10">
                                            <Listbox
                                                aria-label="关注的主播列表"
                                                variant="flat"
                                                selectionMode="none"
                                                itemClasses={{
                                                    base: "py-2 px-2.5 rounded-lg data-[hover=true]:bg-default-200/60 dark:data-[hover=true]:bg-default-100/30 gap-3",
                                                    wrapper: "w-auto flex-1 min-w-0 pr-2",
                                                    title: "min-w-0",
                                                }}
                                                emptyContent={
                                                    <div className="flex flex-col items-center justify-center py-8 text-default-400 gap-1.5">
                                                        <BellOff size={28} className="opacity-40" />
                                                        <span className="text-xs">暂无关注的主播，输入 UID 即可添加开播与动态推送</span>
                                                    </div>
                                                }
                                            >
                                                {livers.map((item, idx) => {
                                                    const info = userInfoMap[item.uid];
                                                    return (
                                                        <ListboxItem
                                                            key={String(item.uid || idx)}
                                                            textValue={info?.name || String(item.uid)}
                                                            startContent={
                                                                <Avatar
                                                                    src={info?.face}
                                                                    name={info?.name ? info.name.slice(0, 2) : String(item.uid).slice(-2)}
                                                                    size="sm"
                                                                />
                                                            }
                                                            title={
                                                                <span className="inline-flex items-center gap-1.5 min-w-0">
                                                                    <span className="font-bold" title={info?.name || `UID: ${item.uid}`}>
                                                                        {info?.name || (info === undefined ? "加载中..." : `UID: ${item.uid}`)}
                                                                    </span>
                                                                    <a
                                                                        href={`https://space.bilibili.com/${item.uid}`}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="text-default-400 hover:text-primary shrink-0 inline-flex items-center"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        title="打开个人空间"
                                                                    >
                                                                        <ExternalLink size={12} />
                                                                    </a>
                                                                </span>
                                                            }
                                                            description={
                                                                <span className="">
                                                                    UID: {item.uid}
                                                                </span>
                                                            }
                                                            endContent={
                                                                <div className="flex items-center gap-2.5 shrink-0 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                                    <Switch
                                                                        size="sm"
                                                                        isSelected={item.live !== false}
                                                                        onValueChange={(val) => handleToggleLiver(idx, 'live', val)}
                                                                        classNames={{ label: "text-xs whitespace-nowrap select-none" }}
                                                                    >
                                                                        开播
                                                                    </Switch>
                                                                    <Switch
                                                                        size="sm"
                                                                        isSelected={item.dynamic !== false}
                                                                        onValueChange={(val) => handleToggleLiver(idx, 'dynamic', val)}
                                                                        classNames={{ label: "text-xs whitespace-nowrap select-none" }}
                                                                    >
                                                                        动态
                                                                    </Switch>
                                                                    <Button
                                                                        isIconOnly
                                                                        size="sm"
                                                                        color="danger"
                                                                        variant="light"
                                                                        className="h-7 w-7 min-w-7 text-default-400 hover:text-danger"
                                                                        onPress={() => handleRemoveLiver(idx)}
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </Button>
                                                                </div>
                                                            }
                                                        />
                                                    );
                                                })}
                                            </Listbox>
                                        </ScrollShadow>
                                    </div>
                                </Tab>

                                {/* 用户关注列表 */}
                                <Tab
                                    key="users"
                                    title={
                                        <div className="flex items-center gap-2">
                                            <User size={15} />
                                            <span>关注用户 ({users.length})</span>
                                        </div>
                                    }
                                >
                                    <div className="flex flex-col gap-3 pt-2">

                                        {/* 快速添加栏 */}
                                        <div className="flex items-center gap-2">
                                            <Autocomplete
                                                aria-label="输入用户名称或 UID"
                                                placeholder="输入目标用户的名称或 UID..."
                                                className="flex-1"
                                                size="sm"
                                                isLoading={userLoading}
                                                inputValue={newUserInput}
                                                onInputChange={(val) => setNewUserInput(val)}
                                                onSelectionChange={(key) => {
                                                    if (key) {
                                                        const item = userSuggestions.find(s => String(s.UID) === String(key));
                                                        handleAddUser(item || { UID: key });
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleAddUser();
                                                    }
                                                }}
                                                allowsCustomValue={true}
                                                isVirtualized={false}
                                                popoverProps={{
                                                    className: "z-[60]"
                                                }}
                                            >
                                                {userSuggestions.map((e) => (
                                                    <AutocompleteItem
                                                        key={String(e.UID)}
                                                        textValue={`${e.UName} UID:${e.UID}`}
                                                    >
                                                        <div className="flex flex-row items-center">
                                                            <div className="flex flex-col">
                                                                <Avatar
                                                                    src={e.face || `${AVATAR_API}${e.UID}`}
                                                                    className="mr-2"
                                                                />
                                                            </div>
                                                            <div>
                                                                <p className="font-bold">{e.UName}</p>
                                                                {e.MedalName ? (
                                                                    <p className="text-small">
                                                                        <Chip
                                                                            startContent={
                                                                                e.MedalType ? (
                                                                                    <img
                                                                                        src={getGuardIcon(e.MedalType)}
                                                                                        className="h-5 w-5"
                                                                                        alt="guard"
                                                                                    />
                                                                                ) : (
                                                                                    <CheckIcon size={18} />
                                                                                )
                                                                            }
                                                                            variant="faded"
                                                                            style={{
                                                                                background: getColor(e.MedalLevel),
                                                                                color: "white",
                                                                                marginTop: "4px",
                                                                            }}
                                                                        >
                                                                            {e.MedalName}
                                                                            <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full">
                                                                                {e.MedalLevel}
                                                                            </span>
                                                                        </Chip>
                                                                    </p>
                                                                ) : (
                                                                    <p className="text-small text-default-400">UID: {e.UID}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </AutocompleteItem>
                                                ))}
                                            </Autocomplete>
                                            <Button
                                                size="sm"
                                                color="primary"
                                                onPress={() => handleAddUser()}
                                                startContent={<Plus size={15} />}
                                                className="shrink-0 h-8"
                                            >
                                                添加
                                            </Button>
                                        </div>

                                        {/* 列表渲染 */}
                                        <ScrollShadow className="w-full max-h-64 border border-default-200/80 dark:border-default-100/30 rounded-xl p-1 bg-default-50/50 dark:bg-default-100/10">
                                            <Listbox
                                                aria-label="关注的用户列表"
                                                variant="flat"
                                                selectionMode="none"
                                                itemClasses={{
                                                    base: "py-2 px-2.5 rounded-lg data-[hover=true]:bg-default-200/60 dark:data-[hover=true]:bg-default-100/30 gap-3",
                                                    wrapper: "w-auto flex-1 min-w-0 pr-2",
                                                    title: "min-w-0",
                                                }}
                                                emptyContent={
                                                    <div className="flex flex-col items-center justify-center py-8 text-default-400 gap-1.5">
                                                        <BellOff size={28} className="opacity-40" />
                                                        <span className="text-xs">暂无关注的用户，输入 UID 即可追踪其弹幕与进房事件</span>
                                                    </div>
                                                }
                                            >
                                                {users.map((item, idx) => {
                                                    const info = userInfoMap[item.uid];
                                                    return (
                                                        <ListboxItem
                                                            key={String(item.uid || idx)}
                                                            textValue={info?.name || String(item.uid)}
                                                            startContent={
                                                                <Avatar
                                                                    src={info?.face}
                                                                    name={info?.name ? info.name.slice(0, 2) : String(item.uid).slice(-2)}
                                                                    size="sm"
                                                                />
                                                            }
                                                            title={
                                                                <span className="inline-flex items-center gap-1.5 min-w-0">
                                                                    <span className="font-bold truncate" title={info?.name || `UID: ${item.uid}`}>
                                                                        {info?.name || (info === undefined ? "加载中..." : `UID: ${item.uid}`)}
                                                                    </span>
                                                                    <a
                                                                        href={`https://space.bilibili.com/${item.uid}`}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="text-default-400 hover:text-primary shrink-0 inline-flex items-center"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        title="打开个人空间"
                                                                    >
                                                                        <ExternalLink size={12} />
                                                                    </a>
                                                                </span>
                                                            }
                                                            description={
                                                                <span className="">
                                                                    UID: {item.uid}
                                                                </span>
                                                            }
                                                            endContent={
                                                                <div className="flex items-center gap-2.5 shrink-0 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                                    <Switch
                                                                        size="sm"
                                                                        isSelected={item.danmaku !== false}
                                                                        onValueChange={(val) => handleToggleUser(idx, 'danmaku', val)}
                                                                        classNames={{ label: "text-xs whitespace-nowrap select-none" }}
                                                                    >
                                                                        弹幕
                                                                    </Switch>
                                                                    <Switch
                                                                        size="sm"
                                                                        isSelected={item.enter !== false}
                                                                        onValueChange={(val) => handleToggleUser(idx, 'enter', val)}
                                                                        classNames={{ label: "text-xs whitespace-nowrap select-none" }}
                                                                    >
                                                                        进房
                                                                    </Switch>
                                                                    <Button
                                                                        isIconOnly
                                                                        size="sm"
                                                                        color="danger"
                                                                        variant="light"
                                                                        className="h-7 w-7 min-w-7 text-default-400 hover:text-danger"
                                                                        onPress={() => handleRemoveUser(idx)}
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </Button>
                                                                </div>
                                                            }
                                                        />
                                                    );
                                                })}
                                            </Listbox>
                                        </ScrollShadow>
                                    </div>
                                </Tab>
                            </Tabs>
                        </ModalBody>

                        <ModalFooter className="">
                            <Button color="primary" variant="flat" onPress={onClose}>
                                完成
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
}

export default PushModal;